import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { ApiEmployee, ApiEmployeeBase } from "@/types/employees/employee.types.ts";
import {
  ApiPermission,
  EmployeeFieldPermission,
  EmployeeFieldPermissionsKeys,
  PermissionsKeys,
  PermissionType,
} from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { useMemo } from "react";

type PermissionEmployee = ApiEmployeeBase | undefined | null;

export default function usePermissions() {
  const { data: employeeMe } = useEmployeeMe();

  function isFeatureEnabled(feature: ApiFeatureType) {
    if (!employeeMe?.plan?.features) return false;
    return !!employeeMe.plan.features[feature]?.enabled;
  }

  function canIView(key: PermissionsKeys) {
    if (!employeeMe) return false;

    return (
      employeeMe.api_permissions[key] === PermissionType.view ||
      employeeMe.api_permissions[key] == PermissionType.edit
    );
  }

  function canIEdit(key: PermissionsKeys) {
    if (!employeeMe) return false;

    return employeeMe.api_permissions[key] == PermissionType.edit;
  }

  function canViewField(employee: PermissionEmployee, field: EmployeeFieldPermissionsKeys) {
    if (employee) {
      // id == 0 — запись ещё не создана: доступ к полям открыт.
      if (employee.id == 0) return true;
      return (
        employee.employee_field_permissions[field] == PermissionType.view ||
        employee.employee_field_permissions[field] == PermissionType.edit
      );
    }
    return false;
  }

  function canEditField(employee: PermissionEmployee, field: EmployeeFieldPermissionsKeys) {
    if (employee) {
      // id == 0 — запись ещё не создана: доступ к полям открыт.
      if (employee.id == 0) return true;
      return employee.employee_field_permissions[field] == PermissionType.edit;
    }
    return false;
  }

  function canViewAnyOfFields(
    employee: PermissionEmployee,
    fields: EmployeeFieldPermissionsKeys[]
  ) {
    return fields.some((field) => canViewField(employee, field));
  }

  function canEditAnyOfFields(
    employee: PermissionEmployee,
    fields: EmployeeFieldPermissionsKeys[]
  ) {
    return fields.some((field) => canEditField(employee, field));
  }

  function canEditAllFields(employee: PermissionEmployee, fields: EmployeeFieldPermissionsKeys[]) {
    return fields.every((field) => canEditField(employee, field));
  }

  function canCurrentEmployeeViewField(field: EmployeeFieldPermission) {
    if (!employeeMe) return false;

    return employeeMe.other_field_permissions_view.includes(field);
  }

  function canCurrentEmployeeEditField(field: EmployeeFieldPermission) {
    if (!employeeMe) return false;

    return employeeMe.other_field_permissions_edit.includes(field);
  }

  function canManageCustomTableForEmployee(
    employee: ApiEmployee | undefined | null,
    tableId: number
  ) {
    if (!employee) return false;

    const table = employee.employee_custom_table_permissions.find(
      (table) => table.table_id == tableId
    );

    if (!table) return false;

    return table.manage_table == PermissionType.edit;
  }

  function canViewCustomTableFieldForEmployee(
    employee: ApiEmployee | undefined | null,
    tableId: number,
    fieldSlug: string
  ) {
    if (!employee) return false;

    const table = employee.employee_custom_table_permissions.find(
      (table) => table.table_id == tableId
    );

    if (!table) return false;

    return (
      table.manage_table == PermissionType.edit ||
      table.field_permissions[fieldSlug] == PermissionType.view ||
      table.field_permissions[fieldSlug] == PermissionType.edit
    );
  }

  function canViewCustomTableChangedByForEmployee(
    employee: ApiEmployee | undefined | null,
    tableId: number
  ) {
    if (!employee) return false;

    const table = employee.employee_custom_table_permissions.find(
      (table) => table.table_id == tableId
    );

    if (!table) return false;

    return table.manage_table == PermissionType.edit || table.changed_by == PermissionType.view;
  }

  function canViewCustomTableForEmployee(
    employee: ApiEmployee | undefined | null,
    tableId: number
  ) {
    if (!employee) return false;

    const table = employee.employee_custom_table_permissions.find(
      (table) => table.table_id == tableId
    );

    if (!table) return false;

    return (
      table.manage_table == PermissionType.edit ||
      table.changed_by == PermissionType.view ||
      Object.values(table.field_permissions).some(
        (permission) => permission == PermissionType.view || permission == PermissionType.edit
      )
    );
  }

  return {
    canViewField,
    canEditField,
    canViewAnyOfFields,
    canEditAnyOfFields,
    canEditAllFields,
    canIView,
    canIEdit,
    canCurrentEmployeeViewField,
    canCurrentEmployeeEditField,
    isFeatureEnabled,
    canManageCustomTableForEmployee,
    canViewCustomTableChangedByForEmployee,
    canViewCustomTableFieldForEmployee,
    canViewCustomTableForEmployee,
  };
}

export const settingsPermissions = [
  ApiPermission.settings_organization,
  ApiPermission.settings_integrations,
  ApiPermission.settings_divisions,
  ApiPermission.settings_departments,
  ApiPermission.settings_positions_levels,
  ApiPermission.settings_locations,
  ApiPermission.settings_legal_entities,
  ApiPermission.settings_employment_types,
  ApiPermission.settings_employment_contracts,
  ApiPermission.settings_compensation_change_reasons,
  ApiPermission.settings_compensation_types,
  ApiPermission.settings_probation_policies,
  ApiPermission.settings_hiring_flows,
  ApiPermission.settings_work_patterns,
  ApiPermission.news_manage_channels,

  ApiPermission.manage_holidays,
  ApiPermission.manage_leave_policies,
  ApiPermission.manage_approval_policy,

  ApiPermission.settings_pipelines,
  ApiPermission.settings_sources,
  ApiPermission.manage_candidates,
  ApiPermission.settings_templates,
  ApiPermission.settings_candidate_disqualification_reasons,
  ApiPermission.settings_job_archiving_reasons,
  ApiPermission.settings_security,
  ApiPermission.settings_organization_links,
  ApiPermission.settings_document_folders,

  ApiPermission.settings_roles_permissions,
  ApiPermission.settings_data_protection,

  ApiPermission.data_import,
];

export const settingsEditFieldPermissions = [EmployeeFieldPermission.manage_employee_termination];

export const usePermissionCheck = () => {
  const { data: employee } = useEmployeeMe();
  const { canIEdit, canCurrentEmployeeEditField } = usePermissions();

  const hasPermissionsToViewSettings = useMemo(() => {
    if (!employee) return false;

    return (
      settingsPermissions.some((permission) => canIEdit(permission)) ||
      settingsEditFieldPermissions.some((permission) => canCurrentEmployeeEditField(permission))
    );
  }, [employee]);

  const canEditWorkflows =
    canIEdit(ApiPermission.workflows_manage_all) || canIEdit(ApiPermission.workflows_create);
  const canViewAllWorkflowExecutions = canIEdit(ApiPermission.workflows_executions);

  return { hasPermissionsToViewSettings, canEditWorkflows, canViewAllWorkflowExecutions };
};
